import { useMemo, useRef } from "react";
import type { GridColDef } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { HeaderFilterCell } from "./HeaderFilterCell";
import type { HeaderFilterModelApi } from "./useHeaderFilterModel";
import { isFilterableColumn } from "./filterOperators";

export function useColumnsWithHeaderFilters(
  columns: GridColDef[],
  filterApi: HeaderFilterModelApi | null,
  enabled: boolean,
): GridColDef[] {
  const filterApiRef = useRef(filterApi);
  filterApiRef.current = filterApi;

  return useMemo(() => {
    if (!enabled || !filterApi) {
      return columns;
    }

    return columns.map((col) => {
      if (!isFilterableColumn(col) || !col.field) {
        // Non-filterable columns (action buttons, id, images…) adopt the same
        // top-aligned title row so every header lines up; they simply omit the
        // filter input below instead of falling back to MUI's centered header.
        const existingRenderHeader = col.renderHeader;
        return {
          ...col,
          renderHeader: (params) => (
            <Box className="header-filter-column">
              <Box className="header-filter-title-row">
                {existingRenderHeader
                  ? existingRenderHeader(params)
                  : params.colDef.headerName}
              </Box>
            </Box>
          ),
        };
      }

      const field = col.field;
      const columnType = col.type;
      const colWithOptions = col as GridColDef & {
        valueOptions?: readonly (
          | string
          | number
          | { value: string | number | boolean; label: string }
        )[];
      };
      const valueOptions = colWithOptions.valueOptions;
      const existingRenderHeader = col.renderHeader;

      return {
        ...col,
        renderHeader: (params) => (
          <Box className="header-filter-column">
            <Box className="header-filter-title-row">
              {existingRenderHeader
                ? existingRenderHeader(params)
                : params.colDef.headerName}
            </Box>
            <Box className="header-filter-row">
              <HeaderFilterCell
                field={field}
                type={columnType}
                width={col.width}
                valueOptions={valueOptions}
                value={filterApiRef.current?.getItemValue(field)}
                operator={
                  filterApiRef.current?.getItemOperator(field, columnType) ??
                  "contains"
                }
                onChange={(f, operator, value) =>
                  filterApiRef.current?.updateItem(
                    f,
                    operator,
                    value,
                    columnType,
                  )
                }
                onClear={(f) => filterApiRef.current?.clearItem(f)}
              />
            </Box>
          </Box>
        ),
      };
    });
    // filterApi is read via ref so column defs stay stable while filter values change.
  }, [columns, enabled]);
}
